/* ── Table IDs ─────────────────────────────────────────────────────────── */
export const BASE = 'appkJsc7Cr5Aht6IO';

export const TBL_DEL           = 'tblFuiDpahqCL4eIK';
export const TBL_PH            = 'tblNl3JpfIEWk9tPX';
export const TBL_CL            = 'tblU6m44TpQ9zkpso';
export const TBL_CLT           = 'tbl6SAgPLXMHm5Uja';
export const TBL_TPL           = 'tblfulsOyOyjOGXab';
export const TBL_BIM           = 'tblpekOAvNFkUsHEj';
export const TBL_SHEET         = 'tblXFwxWKECjoVj9O';
export const TBL_REV           = 'tblBfKyVWRAlW3tmj';
export const TBL_CONCEPT       = 'tblaAwLsCi6XGsdFY';
export const TBL_CONCEPT_UNIT  = 'tblmkEv2v582TifCm';
export const TBL_CONCEPT_LEVEL = 'tblCjGs2HRCAaa9I1';
export const TBL_ROOMS         = 'tbl7sWrKq7bmvctSx';
export const TBL_ROOM_TPL      = 'tblslHjXgMJb59po0';
export const TBL_PROJ          = 'tblgWX330MPJkhQVq';
export const TBL_CONTACTS      = 'tblEcywoAVDwkOLiK';
export const TBL_TIME          = 'tblksH7rbKDI4gWeZ';
export const TBL_AGENCY_APPROVALS = 'tbl2Yu7YtDZ6tFFhI';
export const TBL_DELTA_REVISIONS  = 'tbl2sdOXCdvS2R5DL';

/* ── Deliverable type constants ─────────────────────────────────────────── */
/*
 * 2026-09-22: every one of these was converted from matching on the deliverable type's Name
 * (an Airtable lookup string, editable and NOT stable — see the audit behind this change) to
 * matching on the type's own Airtable record id (Project Deliverable Types, tblfulsOyOyjOGXab —
 * stable regardless of how the type is renamed). A milestone's type id is
 * `getFirst(ms.fields['Deliverable'])`; its current display NAME is still
 * `getFirst(ms.fields['Name (from Deliverable)'])` — use the id for classification, the name only
 * for what a person reads. Every constant below is renamed with an _ID/_IDS suffix so a stray
 * leftover comparison against the old name-keyed constant fails to import (a build error) instead
 * of silently never matching (the exact failure mode this change exists to close off). Each id
 * carries its current Name as a comment for readability only — renaming the type in Airtable does
 * NOT require touching this file.
 *
 * Two real bugs surfaced and fixed by this pass (see PERMIT_MS_TYPE_IDS below): two entries were
 * missing the trailing emoji the real deliverable type Name actually carries, so they never
 * matched anything even before this change.
 *
 * A genuine duplicate was also found: two separate "Submittal Review" deliverable-type records
 * exist in Airtable. Every real Submittal Review milestone (10 checked, 2026-09-22) links
 * recql3s0G1jaNV2AO — SUBMITTAL_REVIEW_TEMPLATE_ID below — the other (recwMCnwqwpXDDsKm) is an
 * unused orphan. Name-based matching was already ambiguous here; this is exactly the kind of bug
 * id-based matching prevents going forward.
 */
export const INTERIOR_CONCEPT_TYPE_ID = 'recqeWggNMMOyoR4p'; // Interior Concept Package
export const PRE_DESIGN_RESEARCH_TYPE_ID = 'recpwPFowkQYX60TE'; // Pre-design Research
export const HOMEWORK_TYPE_ID = 'recdvzGb1F40jUh4j'; // Client Onboarding Homework 🔄 — items ticked with the day they came in (api/_homework.js)
export const RENDERING_PACKAGE_TYPE_ID = 'rec5ZDopLm6O99s8r'; // Rendering Package
export const EXISTING_BUILDING_3D_MODEL_TYPE_ID = 'recwkLhUGJl7d48hf'; // Existing Building 3D Model

// Design-presentation milestone types (Round 10 — modular /present surface).
// A milestone of one of these types opens the matching presentation tool.
export const CONCEPTUAL_FRAMEWORK_TYPE_ID       = 'reco92NDyQJ20iaTa'; // Conceptual Framework
export const INTEGRATED_DESIGN_PROPOSAL_TYPE_ID = 'rec3krW4Ii3yZRoQc'; // Integrated Design Proposal
export const INTERIOR_DESIGN_REFINEMENT_TYPE_ID = 'recjwAzQxqThAEJyt'; // Interior Design Refinement

// Conceptual Framework is NOT in this list (Matt, 2026-10-26): CF is sketches posted in sketch
// ribbons plus the inspiration board, nothing else. Its /present tool still opens by URL
// (PRESENTATION_TYPE_KEY keeps 'cf') but no menu, Documents row or Client Hub link points at it.
export const PRESENTATION_TYPE_IDS = [
  INTEGRATED_DESIGN_PROPOSAL_TYPE_ID,
  INTERIOR_CONCEPT_TYPE_ID,
  INTERIOR_DESIGN_REFINEMENT_TYPE_ID,
];

// Milestone types that mount the Concepts branch (mirrors Reference > Design > Concepts) so a
// concept can be reviewed/added/selected without leaving the milestone. ICP owns the full concept
// hierarchy (Units/Levels/Rooms); Conceptual Framework, Integrated Design Proposal, and the BIM
// Coordination milestones (mirrors BIM_MS_TYPE_IDS below) show the list for reference and
// selection only — the BIM checklist's "chosen concept has been marked Selected" full-kit item is
// what this is for, so the concept it refers to is right there instead of requiring a trip to
// Reference.
export const CONCEPT_BRANCH_MS_TYPE_IDS = [
  INTERIOR_CONCEPT_TYPE_ID,
  CONCEPTUAL_FRAMEWORK_TYPE_ID,
  INTEGRATED_DESIGN_PROPOSAL_TYPE_ID,
  'recGfM7fF5L4NoNOa', // Design Development BIM Coordination
  'recsmVPrqEjDq6Oh8', // Interior Architecture BIM Coordination
  'recnhJGE23xLSvFNi', // Schematic Design BIM Coordination
];

// Milestone types that mount the Criteria branch (mirrors Reference > Design > Criteria) so the
// client's onboarding-questionnaire answers can be reviewed without leaving the milestone.
// Pre-design Research is the original mount; Conceptual Framework needs the same reference since
// its concept work is meant to answer back to the client's stated criteria.
export const CRITERIA_BRANCH_MS_TYPE_IDS = [
  'recpwPFowkQYX60TE', // Pre-design Research
  CONCEPTUAL_FRAMEWORK_TYPE_ID,
];

// Round 46 — Documents section restrictions per milestone type.
// These milestone types' real output already lives somewhere else (a 3D model file, drawing-set
// PDFs tracked by their own section, a spec/selections document) — a manual Documents branch is
// just clutter, so it's removed entirely.
export const DOCS_HIDDEN_MS_TYPE_IDS = [
  'recwkLhUGJl7d48hf', // Existing Building 3D Model
  'recJRA75E5RKIeVBu', // Discretionary Approvals Plan Check Drawings
  'reccrHto8inB7Ycj2', // Permit Plan Check Drawings
  // Architectural Selections Package & Outline Spec removed 2026-09-24 — its Outline Spec +
  // Schedules now live under Documents (SELECTIONS_DOC_KINDS below).
  'recJv58DnUxXKEfo0', // Short Form Residential Specifications
];

// Selections milestones — their Documents branch carries one app-generated row per "kind", each
// marked complete (review copy saved + locked) before the milestone can post, and re-printed from
// what was issued on posting. docs/SELECTIONS_PACKAGE_DOCUMENTS.md. The standalone Specifications /
// Schedules branches are not shown on these types.
export const SELECTIONS_DOC_KINDS = {
  'rec4FpOj0bTvR3fH9': ['outline-spec', 'schedules'], // Architectural Selections Package & Outline Spec
  'reca6Cv2JePucZ4Bp': ['schedules'],                 // Interior Selections Package (a Room Book may join later)
};
export const SELECTIONS_DOC_LABELS = { 'outline-spec': 'Outline Specifications', schedules: 'Schedules' };
/** The document kinds a milestone type carries under Documents ([] for every other type). */
export function selectionsDocKinds(typeId) {
  return (typeId && SELECTIONS_DOC_KINDS[typeId]) || [];
}

// These keep the Documents branch (existing/legacy docs still show, still manageable) but
// disallow new manual uploads — the milestone's real output is an app-generated report,
// presentation or sketch ribbon (Pre-Design Research report, Integrated Design Proposal, the
// Conceptual Framework's sketch ribbons)
// that already has its own dedicated place to live; a manual upload here would just be a second,
// competing copy of the same deliverable.
export const DOCS_UPLOAD_LOCKED_MS_TYPE_IDS = [
  'recpwPFowkQYX60TE', // Pre-design Research
  CONCEPTUAL_FRAMEWORK_TYPE_ID,
  INTEGRATED_DESIGN_PROPOSAL_TYPE_ID,
  'rec4FpOj0bTvR3fH9', // Architectural Selections Package & Outline Spec
  'reca6Cv2JePucZ4Bp', // Interior Selections Package
];

// Deliverable type id → /present ?type= key.
export const PRESENTATION_TYPE_KEY = {
  'reco92NDyQJ20iaTa': 'cf',  // Conceptual Framework
  'rec3krW4Ii3yZRoQc': 'idp', // Integrated Design Proposal
  'recqeWggNMMOyoR4p': 'icp', // Interior Concept Package
  'recjwAzQxqThAEJyt': 'idr', // Interior Design Refinement
};

export const BIM_MS_TYPE_IDS = [
  'recGfM7fF5L4NoNOa', // Design Development BIM Coordination
  'recsmVPrqEjDq6Oh8', // Interior Architecture BIM Coordination
  'recnhJGE23xLSvFNi', // Schematic Design BIM Coordination
];

// Which single BIM_PHASES key a given BIM coordination milestone is scoped to — used to
// restrict the criteria shown under that milestone to just its own phase (Reference > Design >
// BIM Systems still shows every phase). No milestone type maps to 'cd' — CD is drawing
// production, never a coordination round, so 'cd' criteria (if any ever exist) only ever show
// under Reference.
export const BIM_MS_TYPE_PHASE = {
  'recnhJGE23xLSvFNi': 'sd', // Schematic Design BIM Coordination
  'recGfM7fF5L4NoNOa': 'dd', // Design Development BIM Coordination
  'recsmVPrqEjDq6Oh8': 'ia', // Interior Architecture BIM Coordination
};

// 'Plan Check Submission', 'Permit Plan Check Documents', 'Discretionary Approvals Plan Check
// Documents' were in the old name-keyed POST_PERMIT_TYPES but no longer exist as deliverable
// types in Airtable (verified 2026-09-22) — already-dead entries, dropped rather than converted.
export const POST_PERMIT_TYPE_IDS = [
  'reccrHto8inB7Ycj2', // Permit Plan Check Drawings
  'recJRA75E5RKIeVBu', // Discretionary Approvals Plan Check Drawings
];

export const POST_REVISION_TYPE_IDS = [
  'recHf9ezgYszxlPfJ', // PPC Drawings Plan Check Revision
  'recGcYRX5M5nr7xS9', // PPC Drawings Permit Revision
  'recjet03Rp2kKO3aq', // DAPC Drawings Revisions
  'recO3wapjkgPiP4C9', // Plan Check Resubmission 🔹
];

/* ── Phase → drawing-sheet checklist prefix map ─────────────────────────── */
// Keys are the exact values of the 'Project Phase' field on Project Deliverables.
export const SHEET_PHASE_MAP = {
  'Design Development Phase':      'DD',
  'Discretionary Approvals Phase': 'DAPC',
  'Construction Documents Phase':  'PPC / BID',
  'Architectural Detailing Phase': 'EXT',
  'Interior Architecture Phase':   'INT',
}

// Fallback: infer drawing-release prefix from the milestone's deliverable TYPE name.
// Used when Project Phase is blank or non-standard (e.g. a second instance of a
// DAPC milestone placed in an unusual phase slot).
// 2026-09-22: re-keyed from deliverable-type Name to id. Several of the old name keys ('Design
// Development Drawings/Documents', 'Discretionary Approvals Plan Check Documents', 'Permit Plan
// Check Documents', 'PPC Drawings Revisions', 'DAPC Drawings Permit Revision', 'Plan Check
// Submission') don't correspond to any deliverable type that still exists in Airtable — already-
// dead entries, dropped. 'Plan Check Resubmission' was missing its real type's trailing emoji
// (same bug found elsewhere in this pass) — fixed below.
export const SHEET_TYPE_MAP = {
  'recJRA75E5RKIeVBu': 'DAPC',       // Discretionary Approvals Plan Check Drawings
  'reccrHto8inB7Ycj2': 'PPC / BID',  // Permit Plan Check Drawings
  'recGcYRX5M5nr7xS9': 'PPC / BID',  // PPC Drawings Permit Revision
  'recHf9ezgYszxlPfJ': 'PPC / BID',  // PPC Drawings Plan Check Revision
  'recO3wapjkgPiP4C9': 'PPC / BID',  // Plan Check Resubmission 🔹 (bug fix — see note above)
  'recjet03Rp2kKO3aq': 'DAPC',       // DAPC Drawings Revisions
  'recwkLhUGJl7d48hf': 'AB',         // Existing Building 3D Model
}

/** Sheet-checklist phase-prefix for a milestone, or null to disable that filter entirely.
 * Revision milestones (DAPC/PC/Permit Revisions — POST_REVISION_TYPE_IDS) never get a working
 * checklist filter: the phase-prefix concept only means something against a real major
 * release, not a revision round, so the filtered items are just wrong. Returning null here
 * also disables the sheet upload's checklist-completeness gate for these milestones (see
 * RevisionRow.checklistBlocksUpload in DrawingSheetList.jsx — it short-circuits on
 * `!milestonePhasePrefix`) — same root cause, one fix. See docs/PLAN_CHECK.md, 2026-07. */
export function resolveMilestonePhasePrefix(phaseName, typeId) {
  if (POST_REVISION_TYPE_IDS.includes(typeId)) return null
  return SHEET_PHASE_MAP[phaseName] ?? SHEET_TYPE_MAP[typeId] ?? null
}

// Milestone types that issue drawing sheets — the only ones AlderCAD's "Send to a milestone"
// offers (Matt, 2026-10-26: the picker listed every unposted milestone, agency reviews and site
// reviews included). Verified against Project Deliverable Types, 2026-10-26. A milestone that
// already carries the sheet is offered too, whatever its type (except BIM Coordination).
export const DRAWING_SHEET_MS_TYPE_IDS = new Set([
  'recwkLhUGJl7d48hf', // Existing Building 3D Model
  'recJRA75E5RKIeVBu', // Discretionary Approvals Plan Check Drawings
  'recjet03Rp2kKO3aq', // DAPC Drawings Revisions
  'reccrHto8inB7Ycj2', // Permit Plan Check Drawings
  'recHf9ezgYszxlPfJ', // PPC Drawings Plan Check Revision
  'recGcYRX5M5nr7xS9', // PPC Drawings Permit Revision
  'recO3wapjkgPiP4C9', // Plan Check Resubmission 🔹
  'recK42nXSQEVymUAp', // EXT Drawings
  'rec5nqHTvd4NygWsm', // EXT Drawings Revisions
  'recuGVhOLlYTuAjnh', // INT Drawings
  'rectse9I2vIyW6SUG', // INT Drawing Revisions
  'rec1jv3C23CwvIfQu', // Bid Drawings
  'recY3PnnFrTkfxWIq', // Bid Drawings Revisions
  'reccFDFXkuUpN7nYV', // Conformed Drawing Set
  'recd3EN9Ccp3MWuH3', // Architects Supplemental Instructions
  'recYJ26aU8cDuiTib', // Client Requested Design Revision - Drawings
]);

// Round 14 — Spec release milestones + their release MODE.
// A spec is edited TOWARD one of these milestones (the "target"). The mode decides
// what a major release at that milestone issues:
//   outline → the per-section outline_summary (Architectural Selections Package)
//   full    → the full flattened lines (Short Form Residential Specifications)
//   delta   → only the sections changed at that milestone (Bid Addendum / ASI / future Proposal Request)
// Permit / plan-check revisions are intentionally excluded — specs are client/contractor facing.
// Verified against Project Deliverable Types (tblfulsOyOyjOGXab), 2026-09-22.
// NOTE: this whitelist must cover EVERY spec-bearing deliverable type the firm uses, or a project
// whose spec milestone is an un-listed type shows an empty "Select release milestone…" target and
// can never assign a milestone to its spec. The firm's spec-bearing types are the six below
// (full=complete spec, outline=selections/outline-level, delta=incremental change release).
export const SPEC_RELEASE_MS_TYPE_IDS = {
  'rec4FpOj0bTvR3fH9': 'outline', // Architectural Selections Package & Outline Spec
  'reca6Cv2JePucZ4Bp': 'outline', // Interior Selections Package
  'recJv58DnUxXKEfo0': 'full',    // Short Form Residential Specifications
  'recHXZnhdTpAg8ZUF': 'full',    // Three Part Technical Specifications
  'recgZZEBfQahLuc7V': 'delta',   // Bid Addendum
  'recd3EN9Ccp3MWuH3': 'delta',   // Architects Supplemental Instructions
};

/** True if a milestone's deliverable-type id is a spec-release milestone. */
export function isSpecReleaseType(id) {
  return !!id && Object.prototype.hasOwnProperty.call(SPEC_RELEASE_MS_TYPE_IDS, id);
}

// The one milestone deliverable type that's 1:1 with a Supabase `asis` row (construction module,
// architect-driven). Shared by AddAsiModal.jsx (ASI -> milestone), AddMilestoneWizard.jsx
// (milestone -> ASI, the reverse creation path), and MilestoneSections.jsx (the reverse display
// mount) — keep it here so all three read the exact same id.
export const ASI_TEMPLATE_ID = 'recd3EN9Ccp3MWuH3'; // Architects Supplemental Instructions

/** True if a milestone's deliverable-type id (Deliverable[0]) is the ASI template. */
export function isAsiMilestoneType(id) {
  return id === ASI_TEMPLATE_ID;
}

// RFIs and Submittals run the same way as ASIs (1:1 with a Supabase rfis/submittals row via
// milestone_airtable_id) — see docs/PASSENGERS.md's "New milestone types for exit 4" for where
// these templates came from. Shared by AddRfiModal.jsx/AddSubmittalModal.jsx (item -> milestone),
// AddMilestoneWizard.jsx (milestone -> item, the reverse creation path), api/passengers.js
// (triage's "new milestone" exit), and MilestoneSections.jsx (the reverse display mount).
export const RFI_RESPONSE_TEMPLATE_ID = 'rec2YwWMVUNjaExbb'; // RFI Response
export const SUBMITTAL_REVIEW_TEMPLATE_ID = 'recql3s0G1jaNV2AO'; // Submittal Review — see the duplicate-record note above

/** True if a milestone's deliverable-type id is the RFI Response template. */
export function isRfiMilestoneType(id) {
  return id === RFI_RESPONSE_TEMPLATE_ID;
}

/** True if a milestone's deliverable-type id is the Submittal Review template. */
export function isSubmittalMilestoneType(id) {
  return id === SUBMITTAL_REVIEW_TEMPLATE_ID;
}

/** The release mode ('outline'|'full'|'delta') for a deliverable-type id, or null. */
export function specReleaseMode(id) {
  return (id && SPEC_RELEASE_MS_TYPE_IDS[id]) || null;
}

// Construction Gates Phase B (docs/CONSTRUCTION_GATES_PLAN.md) — milestone types that get a Field
// Observation document (checklist-guided site walk capture, frozen into Documents + Client
// Journey on post). The five design-advocacy gate reviews + the ad-hoc Field Report type, which
// use the same tool. Substantial Completion was removed (2026-08) — its site-walk documentation
// is the Punch List (the dedicated `punchlist` provider row), so a Field Observation Report was a
// redundant document. Shared by milestoneDocumentProviders.js (the Documents row) and
// PostMilestoneModal.jsx (the freeze call).
export const FIELD_OBSERVATION_MS_TYPE_IDS = [
  'recPcZhIw9HxUxR1O', // Foundation & Layout Review
  'recGDxb0cdxMlZa05', // Framing & Spatial Review
  'recn2ynYOpbsSm4kQ', // Rough-In / Pre-Cover Review
  'recbCV4kOUGtrkdJa', // Envelope & Waterproofing Review
  'recj2XRVYKOwcreVu', // Finish Layout & Mock-Up Review
  'recd6NVQcupxuwvJt', // Field Report
];

// Punch List (Construction Gates Phase D — room-based reorg, 2026-08) — fixed, non-room top-level
// site-area categories, shown alongside the project's Selected Concept's own Rooms and Spaces
// (fetched live from api/punch-items.js, never hardcoded here — see PunchCapture.jsx). Keys mirror
// the 'room:'+id convention outline/design.js already uses for room node keys. Kept in sync with
// PUNCH_SITE_LABELS in api/_punchRooms.js — update both together.
export const PUNCH_SITE_CATEGORIES = [
  { key: 'site:front-yard', label: 'Front Yard' },
  { key: 'site:rear-yard', label: 'Rear Yard' },
  { key: 'site:left-side-yard', label: 'Left Side Yard' },
  { key: 'site:right-side-yard', label: 'Right Side Yard' },
  { key: 'site:courtyard', label: 'Courtyard' },
  { key: 'site:terrace', label: 'Terrace' },
];

// Schedule-bearing milestone types — same set + modes as specs (Schedule Builder, Round 18).
// Schedules appear under these milestones with their own editor, issued on milestone post.
// outline = client review/approval (not contractor-visible); full = first contractor-visible
// release; delta = revisions/addenda (bring forward only the items being changed).
export const SCHEDULE_RELEASE_MS_TYPE_IDS = {
  'rec4FpOj0bTvR3fH9': 'outline', // Architectural Selections Package & Outline Spec
  'reca6Cv2JePucZ4Bp': 'outline', // Interior Selections Package
  'recJv58DnUxXKEfo0': 'full',    // Short Form Residential Specifications
  'recHXZnhdTpAg8ZUF': 'full',    // Three Part Technical Specifications
  'recgZZEBfQahLuc7V': 'delta',   // Bid Addendum
  'recd3EN9Ccp3MWuH3': 'delta',   // Architects Supplemental Instructions
};

/** True if a milestone's deliverable-type id is a schedule-release milestone. */
export function isScheduleReleaseType(id) {
  return !!id && Object.prototype.hasOwnProperty.call(SCHEDULE_RELEASE_MS_TYPE_IDS, id);
}

/** The schedule release mode ('outline'|'full'|'delta') for a deliverable-type id, or null. */
export function scheduleReleaseMode(id) {
  return (id && SCHEDULE_RELEASE_MS_TYPE_IDS[id]) || null;
}

// Permit-related milestone types (for mounting permits under milestones)
// Discretionary Approvals Plan Check Drawings is intentionally excluded (2026-07): the
// permit a drawing set is submitted under isn't known/assignable until the milestone is
// posted (see PostMilestoneModal's Agency Approval picker), so the Permits branch never
// had anything actionable to show during the milestone's own completion.
//
// Two bugs fixed 2026-09-22: the old name-keyed list had 'Permit Revision Submission' and
// 'Plan Check Resubmission' with NO trailing emoji, but the real deliverable-type Names carry
// one ('Permit Revision Submission 🔹' / 'Plan Check Resubmission 🔹') — those two entries never
// matched anything. Also dropped 'Plan Check Submission', 'Permit Plan Check Documents',
// 'Discretionary Approvals Plan Check Documents' — no longer exist as deliverable types.
export const PERMIT_MS_TYPE_IDS = [
  'recBYeWERIpSBMfrm', // Agency Review 🔄
  'reccrHto8inB7Ycj2', // Permit Plan Check Drawings
  'recMed65PtHVDmxpc', // PPC Initial Agency Submission 🔹
  'recXhNJUfXhYgYDew', // DAPC Initial Agency Submission 🔹
  'recKSL31A0pYA3rjX', // Permit Revision Submission 🔹 (bug fix — see note above)
  'recO3wapjkgPiP4C9', // Plan Check Resubmission 🔹 (bug fix — see note above)
  'recjet03Rp2kKO3aq', // DAPC Drawings Revisions
];

// Bid campaign milestone types (for mounting bid campaigns + creation trigger)
export const BID_CAMPAIGN_MS_TYPE_ID  = 'receWu00C3Q2Z15ld'; // Bid Campaign
export const BID_ADDENDUM_MS_TYPE_ID  = 'recgZZEBfQahLuc7V'; // Bid Addendum

// Value Engineering milestone type (for future mounting)
export const VALUE_ENGINEERING_MS_TYPE_ID = 'recUctEUpuFGKSQ4H'; // Value Engineering

// Reference branch — the collection sections living under the project-level
// "Reference" node (the project runs on the Schedule; these are recall/reference
// views, mounted into the Schedule where relevant). Order = display order.
export const REFERENCE_ROOTS = ['team', 'meetings', 'threads', 'documents', 'design', 'production', 'permits', 'construction'];

/* ── Checklist ──────────────────────────────────────────────────────────── */
export const NOT_REQUIRED_REASONS = [
  'Not applicable to this project',
  'Handled in previous phase',
  'Client has waived this requirement',
  'Covered by consultant'
];

/* ── The Full-Kit gate ──────────────────────────────────────────────────────
 * Renamed from "Pre-Work" on 2026-09-06 (Matt, while grading Rule 1 of Goldratt's Rules of Flow —
 * docs/RULES_OF_FLOW.md): the gate is named after the rule it enforces. The full kit is what has to
 * be in hand before work is released.
 *
 * TWO CONSTANTS, ON PURPOSE. `CAT_FULL_KIT` is the value STORED in Airtable's `Category`
 * singleSelect and matched against on read; `CAT_FULL_KIT_LABEL` is what a person reads. They differ
 * only while Airtable still holds the old option name. Rename that option (and any rollup condition
 * that filters on it — `Incomplete/Completed Pre Work Checklist Items` on Project Deliverables) and
 * the two collapse into one: set CAT_FULL_KIT to 'Full-Kit' and delete catLabel's special case.
 * See docs/FULL_KIT_RENAME.md.
 *
 * Never render a raw category string. Route every display through `catLabel`. */
export const CAT_FULL_KIT = 'Pre-Work';
export const CAT_FULL_KIT_LABEL = 'Full-Kit';
export const catLabel = (c) => (c === CAT_FULL_KIT ? CAT_FULL_KIT_LABEL : c);

// Gate order, by the STORED category value. Keys are Airtable values, never labels.
export const CAT_SORT_ORDER = { [CAT_FULL_KIT]: 1, 'Execution': 2, 'QA/QC': 3, 'Coordination': 3, 'Wrap-Up': 4 };

// The four gates in order, as stored. Pair with catLabel for anything a person reads.
export const CAT_ORDER = [CAT_FULL_KIT, 'Execution', 'QA/QC', 'Wrap-Up'];

// Checklist item categories hidden from milestone checklist rendering (2026-09) — QA/QC items
// stay in Airtable and keep driving whatever still reads them there, but the milestone
// checklist branch no longer shows them: that work is migrating to AlderQC (docs/QC_WORKFLOW.md)
// and showing it in both places was noise. Filtered at the item level (before category grouping
// and before the "Blocks Progression" chain runs) so a hidden QA/QC item can never silently hold
// up a later Wrap-Up item as an invisible blocker.
// CAT_FULL_KIT joined 2026-09-10 (Matt): the six gates (docs/GATES.md) are the full kit now, checked
// interactively at takeoff (GateCheckModal.jsx). The old Pre-Work items on Airtable checklists are
// hidden, no longer count toward posting or fast track, and were marked Not Required in bulk.
export const HIDDEN_CL_CATEGORIES = ['QA/QC', CAT_FULL_KIT];

/* ── Concepts / Rooms ───────────────────────────────────────────────────── */
export const SCOPE_TYPE_OPTIONS = [
  'New Construction', 'Cosmetic Refresh', 'Light Alteration', 'Moderate Alteration',
  'Heavy Alteration', 'Major Alteration', 'Existing to Remain'
];

// One-line (well — a few sentences, for the alteration tiers) help text shown next to the Scope
// Type picker so a designer doesn't have to guess what each tier is meant to cover. The % is the
// tier's cost factor — how much of a full ground-up rebuild of that same room this scope
// represents. Keep in sync with each Control Budget Model's own factor fields (Light/Moderate/
// Heavy/Major Alteration Factor, Cosmetic Refresh Factor, Existing to Remain Factor) — this text
// is descriptive only, not itself a source of the numbers. Also shown (read-only, next to the
// matching factor input) in ControlBudgetModelsEditor.jsx.
//
// Round 5 — Moderate/Heavy carry explicit guidance for the "remodel + addition in the same room"
// case (a room whose existing shell is altered while also receiving new structural square
// footage, e.g. a bump-out under a new roofline): the room stays ONE Room Book entry (rooms link
// to finishes/fixtures/schedules and — eventually — Revit model rooms, so splitting one physical
// room into two Room records was ruled out), and the addition's share of the room's total
// (existing + addition) footprint is what moves it up the ladder — not a separate field, just
// guidance for picking the single Scope Type that already exists.
export const SCOPE_TYPE_DEFINITIONS = {
  'New Construction': '100% of rebuild cost — entirely new space, full ground-up scope (framing, MEP, finishes).',
  'Cosmetic Refresh': '~10% of rebuild cost — surface-only: paint, flooring, fixture/hardware swaps. No wall moves, no structural or MEP changes, no addition.',
  'Light Alteration': '25% of rebuild cost — minor layout tweaks, some new finishes, light electrical/plumbing relocation. No structural changes, no addition.',
  'Moderate Alteration': '50% of rebuild cost — meaningful finish updates and some MEP/layout changes, without major structural work. Also covers a SMALL addition to the room — roughly ≤25% of its total (existing + addition) footprint being new construction, with the existing portion getting moderate finish/MEP work.',
  'Heavy Alteration': '75% of rebuild cost — significant renovation: some structural/layout changes, most finishes and MEP replaced, short of a full gut. Also covers a LARGER addition — roughly 25–50% of the room’s total footprint being new construction.',
  'Major Alteration': '125% of rebuild cost — full gut renovation, or an addition exceeding roughly half the room’s total footprint. Reserved for situations where altering genuinely costs more than building new: unknown/deteriorated existing conditions requiring extensive remediation, code-triggered upgrades (seismic, egress, accessibility) that cascade beyond the visible scope, or an addition so large that integrating it with the remaining existing shell costs more than a clean rebuild would.',
  'Existing to Remain': '0% — this space isn’t touched at all, so it carries no cost.',
};

export const UNIT_TYPE_OPTIONS = [
  'Single-Family Residence', 'ADU Attached', 'ADU Detached',
  'ADU Conversion', 'Living Unit', 'Attached Garage Apartment'
];

/* Milestone deliverable type whose Design node shows the section labeled "Design" (sec:<msId>:design-concept)
   instead of "Concepts" (sec:<msId>:concepts) — used by MilestoneSections.jsx and the breadcrumb builder
   in outline.js so entity-detail zooms reached from this milestone type link back correctly. */
export const DAPC_DRAWINGS_MS_TYPE_ID = 'recJRA75E5RKIeVBu'; // Discretionary Approvals Plan Check Drawings
// The milestones that offer the DRB presentation (docs/DRB_PRESENTATION.md): the DAPC set, its
// revisions, and the two hearing milestones (api/_keyDates.js#HEARING_TYPE_IDS).
export const DRB_PRESENTATION_MS_TYPE_IDS = [
  'recJRA75E5RKIeVBu', // Discretionary Approvals Plan Check Drawings
  'recjet03Rp2kKO3aq', // DAPC Drawings Revisions
  'recISI3ASEiqaZDHf', // Public Hearing Preparation
  'recdfB44iGUTVMuNo', // Public Hearing
  'recIvEm4Q6ZAv8VnF', // Appeal Hearing Preparation (the appeal's presentation, database/360)
];

/* ── Add Milestone wizard ───────────────────────────────────────────────── */
export const SPOKES = ['All', 'Architecture', 'Interior', 'Landscape'];

export const PHASE_NAME_TO_TPL = {
  'Pre-design & Planning Phase':         'Pre-design',
  'Pre-Design & Planning Phase':         'Pre-design',
  'Schematic Design Phase':              'Schematic Design',
  'Discretionary Approvals Phase':       'Discretionary Approvals',
  'Design Development Phase':            'Design Development',
  'Construction Documents Phase':        'Construction Documents',
  'Permitting Phase':                    'Permitting',
  'Architectural Detailing Phase':       'Architectural Detailing',
  'Interior Architecture Phase':         'Interior Architecture',
  'Bidding and Negotiation Phase':       'Bidding and Negotiation',
  'Construction Support Phase':          'Construction Support',
  'Design Consultation Phase':           'Schematic Design',
  'Existing Building 3D Model Phase':    'Existing Building 3D Model'
};

/* ── Icons ──────────────────────────────────────────────────────────────── */
export const ICO_TIME     = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><polyline points="8,4.5 8,8 10.5,10" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
export const ICO_LINK     = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 8.5a3.5 3.5 0 0 0 5 0l1.5-1.5a3.5 3.5 0 0 0-5-5L6 3.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M10 7.5a3.5 3.5 0 0 0-5 0L3.5 9A3.5 3.5 0 0 0 8.5 14L10 12.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
export const ICO_EDIT     = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M11 2.5l2.5 2.5-8 8H3v-2.5l8-8z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
export const ICO_ADD      = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><line x1="8" y1="5" x2="8" y2="11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><line x1="5" y1="8" x2="11" y2="8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
export const ICO_DATE     = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="2.5" width="13" height="12" rx="2" stroke="currentColor" stroke-width="1.3"/><line x1="1.5" y1="6.5" x2="14.5" y2="6.5" stroke="currentColor" stroke-width="1.3"/><line x1="5" y1="1" x2="5" y2="4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><line x1="11" y1="1" x2="11" y2="4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
export const ICO_CHECK    = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><polyline points="2.5,8.5 6,12 13.5,4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
export const ICO_SELECT   = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.3"/><rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.3"/></svg>';
export const ICO_SKIP     = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><line x1="5" y1="5" x2="11" y2="11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><line x1="11" y1="5" x2="5" y2="11" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
export const ICO_EXPAND   = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polyline points="3,6 8,11 13,6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><polyline points="3,2 8,7 13,2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
export const ICO_COLLAPSE = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polyline points="3,10 8,5 13,10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><polyline points="3,14 8,9 13,14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
export const ICO_SLACK    = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M5.5 2a1.5 1.5 0 0 0 0 3H7V2H5.5z" stroke="currentColor" stroke-width="1.2"/><path d="M2 5.5a1.5 1.5 0 0 0 3 0V4H2v1.5z" stroke="currentColor" stroke-width="1.2"/><path d="M10.5 14a1.5 1.5 0 0 0 0-3H9v3h1.5z" stroke="currentColor" stroke-width="1.2"/><path d="M14 10.5a1.5 1.5 0 0 0-3 0V12h3v-1.5z" stroke="currentColor" stroke-width="1.2"/><path d="M14 5.5a1.5 1.5 0 0 0-3 0H9.5v1.5H14z" stroke="currentColor" stroke-width="1.2"/><path d="M10.5 2a1.5 1.5 0 0 0 0 3V2z" stroke="currentColor" stroke-width="1.2"/><path d="M2 10.5a1.5 1.5 0 0 0 3 0V9H2v1.5z" stroke="currentColor" stroke-width="1.2"/><path d="M5.5 14a1.5 1.5 0 0 0 0-3H4v1.5a1.5 1.5 0 0 0 1.5 1.5z" stroke="currentColor" stroke-width="1.2"/></svg>';
export const ICO_COPY     = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="5.5" y="1.5" width="9" height="11" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M3.5 4.5H2.5A1 1 0 0 0 1.5 5.5v9a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
export const ICO_PIN      = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M9.5 1.5l5 5-2 1-3-3-4 4 1 1-3 5-2-2 5-3 1 1 4-4-3-3 1-2z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><line x1="2" y1="14" x2="5" y2="11" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>';
export const ICO_TEMPLATE = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="13" height="13" rx="2" stroke="currentColor" stroke-width="1.3"/><line x1="1.5" y1="5.5" x2="14.5" y2="5.5" stroke="currentColor" stroke-width="1.3"/><line x1="5" y1="9" x2="11" y2="9" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><line x1="5" y1="11.5" x2="9" y2="11.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';
export const ICO_INFO     = '<svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3"/><rect x="7.3" y="6.5" width="1.4" height="4.5" rx=".7" fill="currentColor"/><rect x="7.3" y="4" width="1.4" height="1.4" rx=".7" fill="currentColor"/></svg>';
